import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Country } from 'constants/country'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { getPropertyAddressFromGooglePlace } from 'utils/location/googlePlaceUtils'
import { logger } from 'utils/logging'
import { lookupPlaceFromAddress } from 'utils/helpers/lookUpPlaceFromAddress'
import { missingAddressTextFields } from './usePropertyCompleteness'
import { AddressSearchInput } from 'components/common/AddressSearchInput'
import { PfRevampEventName } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'

export interface AutofillAddressData {
  isManual: false
  address: string
  propertyAddress: ReturnType<typeof getPropertyAddressFromGooglePlace>
}

export interface ManualAddressData {
  isManual: true
  address: string
  street: string
  streetNumber: string
  city: string
  postalCode: string
  country: string
  /** Where the property is, as far as we can tell. */
  coordinates?: { lat: number; lng: number }
  /**
   * The closest ROUTABLE point for this address.
   *
   * A hand-typed address is often one the map does not carry, so the silent
   * geocode returns the nearest thing it can resolve — a street, a locality
   * centroid. That result is a routing pin, not the building, and it used to be
   * written into `coordinates` alone: the property then displayed at whatever
   * was snapped to and the distinction was unrecoverable. Emitted separately so
   * the caller can persist it as `creativeRoutingAddress` and leave the address
   * free to carry a precise point whenever one is known.
   */
  routingCoordinates?: { lat: number; lng: number }
}

export type AddressData = AutofillAddressData | ManualAddressData

/** A Places detail result in the shape `getPropertyAddressFromGooglePlace` reads. */
const toPropertyAddress = (place: google.maps.places.PlaceResult) =>
  getPropertyAddressFromGooglePlace({
    formatted_address: place.formatted_address ?? '',
    geometry: {
      location: {
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      },
    },
    place_id: place.place_id ?? '',
    address_components: place.address_components ?? [],
  })

interface Props {
  showErrors: boolean
  onAddressChange: (data: AddressData | null) => void
  onManualModeChange: (isManual: boolean) => void
  /** Starts the address search with this text and shows its suggestions. */
  initialQuery?: string
  /** Called once, when the client first edits the search text. */
  onQueryEdited?: () => void
}

/**
 * Address entry section for the PF revamp PropertySelectionDrawer.
 *
 * Supports two sub-modes:
 * - Google Places autocomplete (default)
 * - Manual address entry with silent geocoding fallback
 *
 * Calls `onAddressChange` whenever the address state changes so the parent
 * form can derive validity.
 */
export const AddressSection: React.FC<Props> = ({ showErrors, onAddressChange, onManualModeChange, initialQuery = '', onQueryEdited }) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'order', 'country'])

  const [isManualMode, setIsManualMode] = useState(false)

  // Autofill state
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null)
  const [autofillAddress, setAutofillAddress] = useState('')
  const [autofillError, setAutofillError] = useState('')
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const isQueryEditedRef = useRef(false)

  // Manual address state
  const [street, setStreet] = useState('')
  const [streetNumber, setStreetNumber] = useState('')
  const [city, setCity] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [country, setCountry] = useState<string>('')
  const [manualGeocodedPlace, setManualGeocodedPlace] = useState<{ lat: number; lng: number } | null>(null)
  /** Set when the manual fields were opened by an incomplete Places pick rather
   *  than by the user: drives the hint, and marks the empty boxes straight away
   *  instead of waiting for a submit the button would not allow anyway. */
  const [isCompletingPlace, setIsCompletingPlace] = useState(false)

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    if (!isQueryEditedRef.current) {
      isQueryEditedRef.current = true
      onQueryEdited?.()
    }

    if (selectedPlace) {
      setSelectedPlace(null)
      setAutofillAddress('')
    }
  }, [selectedPlace, onQueryEdited])

  const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
    const address = place.formatted_address ?? searchQuery

    // Google returns only the components it holds — a street with no house
    // numbers on the map yields `streetNumber: ''`, which the BE rejects at
    // session-create. Carry the pick into the manual fields instead of
    // emitting it: the user completes the one missing box, keeps the
    // coordinates Google resolved, and never reaches a dead end.
    const propertyAddress = toPropertyAddress(place)
    if (propertyAddress && missingAddressTextFields(propertyAddress).length > 0) {
      setStreet(propertyAddress.street)
      setStreetNumber(propertyAddress.streetNumber)
      setCity(propertyAddress.city)
      setZipCode(propertyAddress.postalCode)
      setCountry(propertyAddress.country)
      setManualGeocodedPlace(propertyAddress.coordinates ?? null)
      setIsCompletingPlace(true)
      // Dropped so returning to search cannot re-emit the incomplete pick.
      setSelectedPlace(null)
      setAutofillAddress('')
      setSearchQuery(address)
      handleSetManualMode(true)
      return
    }

    setSelectedPlace(place)
    setAutofillAddress(address)
    setSearchQuery(address)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])


  const manualDisplayAddress = useMemo(() => {
    return [
      `${street} ${streetNumber}`.trim(),
      city,
      zipCode,
      country,
    ].filter(Boolean).join(', ')
  }, [street, streetNumber, city, zipCode, country])

  const geocodeManualAddress = useCallback(async () => {
    if (!street || !streetNumber || !city || !zipCode || !country) return

    try {
      const addressString = `${street} ${streetNumber}, ${city}, ${zipCode}, ${country}`
      const lookupPlace = await lookupPlaceFromAddress(addressString)

      if (lookupPlace?.geometry?.location) {
        setManualGeocodedPlace({
          lat: lookupPlace.geometry.location.lat,
          lng: lookupPlace.geometry.location.lng,
        })
      }
    } catch (error) {
      logger.error('Failed to geocode manual address', error, {
        context: { component: 'AddressSection', action: 'geocodeManualAddress' },
      })
    }
  }, [street, streetNumber, city, zipCode, country])

  const handleManualFieldBlur = useCallback(() => {
    geocodeManualAddress()
  }, [geocodeManualAddress])

  useEffect(() => {
    geocodeManualAddress()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country])

  /** The gaps in a diverted Places pick are shown as errors on sight — the user
   *  did not type them, and the submit button they would learn from is disabled. */
  const highlightErrors = showErrors || isCompletingPlace

  const handleSetManualMode = useCallback((manual: boolean) => {
    setIsManualMode(manual)
    onManualModeChange(manual)
    // Typing an address by hand means search could not find the property — a
    // data-quality signal as much as a funnel one, which is why legacy counted
    // both directions rather than only the switch into manual entry.
    dispatchPfRevampEvent({
      type: PfRevampEventName.ADDRESS_ENTRY_MODE_CHANGED,
      payload: { manual },
    })
  }, [onManualModeChange])

  // Notify parent whenever address data changes
  useEffect(() => {
    if (isManualMode) {
      const allFilled = !!country
        && missingAddressTextFields({ street, streetNumber, postalCode: zipCode, city }).length === 0

      onAddressChange(allFilled ? {
        isManual: true,
        address: manualDisplayAddress,
        street,
        streetNumber,
        city,
        postalCode: zipCode,
        country,
        coordinates: manualGeocodedPlace ?? undefined,
        // Same value today — the geocode of a typed address IS the snap. Carried
        // under its own name so persisting it does not claim the property sits
        // there, and so a later precise pin can replace `coordinates` without
        // disturbing routing.
        routingCoordinates: manualGeocodedPlace ?? undefined,
      } : null)

      return
    }

    if (!selectedPlace) {
      onAddressChange(null)
      return
    }

    const reactGeocodePlace = {
      formatted_address: selectedPlace.formatted_address ?? '',
      geometry: {
        location: {
          lat: selectedPlace.geometry?.location?.lat() ?? 0,
          lng: selectedPlace.geometry?.location?.lng() ?? 0,
        },
      },
      place_id: selectedPlace.place_id ?? '',
      address_components: selectedPlace.address_components ?? [],
    }

    const propertyAddress = getPropertyAddressFromGooglePlace(reactGeocodePlace)

    if (!propertyAddress) {
      setAutofillError(t('order:step_address.address_not_selected'))
      onAddressChange(null)
      return
    }

    // Belt to `handlePlaceSelect`'s braces: an incomplete pick is diverted into
    // the manual fields before it gets here, so this only fires if a place ever
    // reaches `selectedPlace` by another route. Either way it must not be
    // emitted as an address the caller may create a property from.
    if (missingAddressTextFields(propertyAddress).length > 0) {
      setAutofillError(t('purchase_flow_revamp:address_incomplete_hint'))
      onAddressChange(null)
      return
    }

    setAutofillError('')
    onAddressChange({ isManual: false, address: autofillAddress, propertyAddress })
  }, [
    isManualMode, selectedPlace, autofillAddress,
    street, streetNumber, city, zipCode, country,
    manualGeocodedPlace, manualDisplayAddress, onAddressChange, t,
  ])

  return (
    <Stack gap={0.4}>

      <Typography variant="text-sm" fontWeight={450} color={GRAY_700}>
        {t('purchase_flow_revamp:property_address_label')}
      </Typography>

      {!isManualMode &&
        <Box>

          <AddressSearchInput
            value={searchQuery}
            placeholder={t('order:step_address.search_property_placeholder')}
            startIcon={<SearchOutlinedIcon sx={{ fontSize: '1.6rem' }} />}
            onChange={handleSearchChange}
            onSelect={(_, place) => handlePlaceSelect(place)}
            searchOnMount={!!initialQuery}
          />

          {!!autofillError &&
            <Typography variant="text-xs" color={CORAL_600} sx={{ mt: 0.4 }}>
              {autofillError}
            </Typography>
          }

          <Box mt={0.4}>
            <Typography variant="text-sm" color={GRAY_700}>
              {t('purchase_flow_revamp:address_doesnt_exist')}
              {' '}
              <Typography
                component="span"
                variant="text-sm"
                color={GRAY_900}
                fontWeight="bold"
                sx={{ cursor: 'pointer', '&:hover': { color: CORAL_600 } }}
                onClick={() => handleSetManualMode(true)}
              >
                {t('purchase_flow_revamp:enter_manually_link')}
              </Typography>
            </Typography>
          </Box>

        </Box>
      }

      {isManualMode &&
        <Stack gap={0.8}>

          {isCompletingPlace &&
            <Typography variant="text-xs" color={CORAL_600}>
              {t('purchase_flow_revamp:address_incomplete_hint')}
            </Typography>
          }

          <Stack gap={0.8} direction="row">

            <Box flex={1}>
              <MUIInputField
                required
                label={t('order:step_address.address')}
                value={street}
                isError={highlightErrors && !street}
                onChange={(e) => setStreet(e.target.value)}
                onBlur={handleManualFieldBlur}
              />
            </Box>

            <Box flex={1}>
              <MUIInputField
                required
                label={t('order:step_address.street_number')}
                value={streetNumber}
                isError={highlightErrors && !streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                onBlur={handleManualFieldBlur}
              />
            </Box>

          </Stack>

          <Stack gap={0.8} direction="row">

            <Box flex={1}>
              <MUIInputField
                required
                label={t('order:step_address.city')}
                value={city}
                isError={highlightErrors && !city}
                onChange={(e) => setCity(e.target.value)}
                onBlur={handleManualFieldBlur}
              />
            </Box>

            <Box flex={1}>
              <MUIInputField
                required
                label={t('order:step_address.zip_code')}
                value={zipCode}
                isError={highlightErrors && !zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                onBlur={handleManualFieldBlur}
              />
            </Box>

          </Stack>

          <MUIDropdown
            button={(isOpen, action) => (
              <MUIDropdownInput
                readOnly
                required
                isError={highlightErrors && !country}
                label={t('order:step_address.country')}
                type={DropdownInputTypes.SELECTOR}
                value={country ? t(`country:${country}`) : ''}
                onClick={action}
              />
            )}
          >
            <Box height="30rem">
              {Object.values(Country).map((countryKey) => (
                <MUIDropdownItem key={countryKey} onClick={() => setCountry(countryKey)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`country:${countryKey}`, countryKey)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </Box>
          </MUIDropdown>

          <MUIButton
            type="secondaryBorder"
            size="xs"
            onClick={() => {
              setIsCompletingPlace(false)
              handleSetManualMode(false)
            }}
          >
            {t('purchase_flow_revamp:back_to_search_link')}
          </MUIButton>

        </Stack>
      }

    </Stack>
  )
}
